import { EventUpdateResponse } from '@collector/sportsapi-client-legacy'
import { sportsApiClient } from '@desktop/globalState/sportsApiClient'

import { useEvent } from '../../composables'
import { injectEventClock, injectEventClockHelpers } from '../provide-inject'

type UseEventClock = NonNullable<
  ReturnType<typeof injectEventClock> &
    ReturnType<typeof injectEventClockHelpers>
> & {
  updateEventClockTime: () => Promise<EventUpdateResponse>
  updateEventClockRunning: () => Promise<void>
}

/**
 * A composable that should be used by modules needing to interact with the event clock.
 *
 * If a sport doesn't have a clock or hasn't initialized the clock (and called `useEventClock`),
 * the composable will throw an error.
 */
export function useEventClock(): UseEventClock {
  // inject
  const eventClock = injectEventClock()

  if (!eventClock) {
    throw Error(
      `unexpected value of injectEventClock: ${eventClock}, did you forget to call provideEventClock?`,
    )
  }

  const eventClockHelpers = injectEventClockHelpers()
  if (!eventClockHelpers) {
    throw Error(
      `unexpected value of injectEventClockHelpers: ${eventClockHelpers}, did you forget to call provideEventClockHelpers?`,
    )
  }

  const { eventTime, isClockRunning, startClock, stopClock } = eventClock
  const {
    getEventClockTime,
    currentPeriodStartOffsetMinutes,
    isTimerDirectionDown,
  } = eventClockHelpers

  // composables
  const { event } = useEvent()

  // methods
  function patchEventClockTime(
    clockTime: number,
  ): Promise<EventUpdateResponse> {
    return sportsApiClient.Events.patch(event.value.id, {
      event: {
        clock_time: clockTime,
        clock_status: isClockRunning.value,
      },
    })
  }

  async function updateEventClockTime(): Promise<EventUpdateResponse> {
    const clockTime = getEventClockTime(
      eventTime.value.minute,
      eventTime.value.second,
      currentPeriodStartOffsetMinutes.value,
      isTimerDirectionDown.value,
    )

    return await patchEventClockTime(clockTime)
  }

  async function updateEventClockRunning(): Promise<void> {
    if (isClockRunning.value) {
      stopClock()
    } else {
      startClock()
    }
    await updateEventClockTime()
  }

  return {
    ...eventClock,
    ...eventClockHelpers,
    updateEventClockTime,
    updateEventClockRunning,
  }
}
